import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bell, BellOff, MapPin, Tag, AlertTriangle, 
  CheckCircle, Send, Loader2, Globe, Building, Map, RefreshCw, CreditCard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const AccountSettings = ({ user, onUserUpdate }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  
  const [settings, setSettings] = useState({
    enabled: false,
    countries: [],
    states: [],
    cities: [],
    categories: [],
    urgent_only: false,
    notification_types: ['new_signal', 'urgent_signal']
  });
  
  const [hasPushSubscription, setHasPushSubscription] = useState(false);
  const [availableAreas, setAvailableAreas] = useState({
    countries: [],
    states: [],
    cities: []
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [permissionState, setPermissionState] = useState('default');

  useEffect(() => {
    loadSettings();
    loadAvailableAreas();
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setPermissionState(Notification.permission);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/notifications/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data.settings);
      setHasPushSubscription(response.data.has_push_subscription);
      setAvailableCategories(response.data.available_categories || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load notification settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableAreas = async () => {
    try {
      const response = await axios.get(`${API}/notifications/areas`, {
        headers: getAuthHeader()
      });
      setAvailableAreas(response.data);
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Push notifications not supported in this browser');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      toast.error('Service workers not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  };

  const subscribeToPush = async () => {
    try {
      // Get VAPID public key
      const vapidResponse = await axios.get(`${API}/notifications/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.public_key;
      
      if (!vapidPublicKey) {
        toast.error('Push notifications not configured on server');
        return null;
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      const subscriptionJson = subscription.toJSON();
      
      // Send subscription to backend
      await axios.post(
        `${API}/notifications/subscribe`,
        {
          endpoint: subscriptionJson.endpoint,
          keys: subscriptionJson.keys
        },
        { headers: getAuthHeader() }
      );

      setHasPushSubscription(true);
      return subscriptionJson;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Failed to subscribe to push notifications');
      return null;
    }
  };

  const handleToggleNotifications = async (enabled) => {
    if (enabled && permissionState !== 'granted') {
      const granted = await requestNotificationPermission();
      if (!granted) return;
    }

    if (enabled && !hasPushSubscription) {
      const subscription = await subscribeToPush();
      if (!subscription) return;
    }

    setSettings(prev => ({ ...prev, enabled }));
  };

  const handleToggleCategory = (category) => {
    setSettings(prev => {
      const categories = prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category];
      return { ...prev, categories };
    });
  };

  const handleToggleCountry = (country) => {
    setSettings(prev => {
      const countries = prev.countries.includes(country)
        ? prev.countries.filter(c => c !== country)
        : [...prev.countries, country];
      return { ...prev, countries };
    });
  };

  const handleToggleCity = (city) => {
    setSettings(prev => {
      const cities = prev.cities.includes(city)
        ? prev.cities.filter(c => c !== city)
        : [...prev.cities, city];
      return { ...prev, cities };
    });
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await axios.put(
        `${API}/notifications/settings`,
        { preferences: settings },
        { headers: getAuthHeader() }
      );
      toast.success('Notification settings saved');
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendTestNotification = async () => {
    setIsSendingTest(true);
    try {
      await axios.post(
        `${API}/notifications/test`,
        {
          title: 'Test Notification',
          body: 'If you see this, push notifications are working!'
        },
        { headers: getAuthHeader() }
      );
      toast.success('Test notification sent!');
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleRestoreSubscription = async () => {
    setIsRestoring(true);
    try {
      const response = await axios.post(
        `${API}/subscription/restore`,
        {},
        { headers: getAuthHeader() }
      );
      
      const data = response.data;
      
      if (data.status === 'success') {
        toast.success(data.message || 'Subscription restored successfully!');
        // Refresh user data to update UI
        if (onUserUpdate) {
          onUserUpdate();
        }
      } else if (data.status === 'no_customer') {
        toast.info(data.message || 'No Stripe customer found. Please subscribe first.');
      } else if (data.status === 'no_subscription') {
        toast.info(data.message || 'No active subscription found.');
      } else if (data.status === 'inactive') {
        toast.warning(data.message || 'Your subscription is inactive.');
      } else {
        toast.info(data.message || 'Could not restore subscription.');
      }
    } catch (error) {
      console.error('Error restoring subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore subscription');
    } finally {
      setIsRestoring(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="account-settings-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-zinc-200"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg text-zinc-100">Account Settings</h1>
              <p className="text-xs text-zinc-500">{user?.email}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Subscription Status & Restore Section */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-emerald-500" />
            <div>
              <h2 className="font-heading font-semibold text-zinc-100">Subscription</h2>
              <p className="text-xs text-zinc-500">Manage your membership status</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-2">
              {user?.is_subscribed ? (
                <>
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-medium">Active Member</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Not Subscribed</span>
                </>
              )}
            </div>
            {user?.subscription_status && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  user.subscription_status === 'active' 
                    ? 'border-emerald-500/50 text-emerald-400' 
                    : 'border-zinc-600 text-zinc-400'
                }`}
              >
                {user.subscription_status}
              </Badge>
            )}
          </div>
          
          {/* Restore Access Button */}
          <div className="pt-2 border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-zinc-200">Restore Access</h3>
                <p className="text-xs text-zinc-500">
                  Paid but still locked? Click to resync with Stripe.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRestoreSubscription}
                disabled={isRestoring}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-emerald-400 hover:border-emerald-500/50"
                data-testid="restore-subscription-btn"
              >
                {isRestoring ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Restore
                  </>
                )}
              </Button>
            </div>
          </div>
        </section>

        {/* Push Notifications Section */}
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.enabled ? (
                <Bell className="w-5 h-5 text-red-500" />
              ) : (
                <BellOff className="w-5 h-5 text-zinc-500" />
              )}
              <div>
                <h2 className="font-heading font-semibold text-zinc-100">Push Notifications</h2>
                <p className="text-xs text-zinc-500">Get alerts for signals in your area</p>
              </div>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={handleToggleNotifications}
              data-testid="notifications-toggle"
            />
          </div>

          {permissionState === 'denied' && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              Notification permission denied. Please enable notifications in your browser settings.
            </div>
          )}

          {settings.enabled && hasPushSubscription && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              <span>Push notifications active</span>
            </div>
          )}
        </section>

        {/* Urgent Only Toggle */}
        {settings.enabled && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <h3 className="font-medium text-zinc-100">Urgent Alerts Only</h3>
                  <p className="text-xs text-zinc-500">Only receive notifications for urgent signals</p>
                </div>
              </div>
              <Switch
                checked={settings.urgent_only}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, urgent_only: checked }))}
                data-testid="urgent-only-toggle"
              />
            </div>
          </section>
        )}

        {/* Area Preferences */}
        {settings.enabled && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-blue-500" />
              <div>
                <h3 className="font-medium text-zinc-100">Area Preferences</h3>
                <p className="text-xs text-zinc-500">Select areas to receive alerts from (empty = all)</p>
              </div>
            </div>

            {/* Countries */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Countries
              </Label>
              <div className="flex flex-wrap gap-2">
                {availableAreas.countries.length === 0 ? (
                  <span className="text-xs text-zinc-500">No countries available yet</span>
                ) : (
                  availableAreas.countries.map(country => (
                    <Badge
                      key={country}
                      variant={settings.countries.includes(country) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        settings.countries.includes(country)
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                      }`}
                      onClick={() => handleToggleCountry(country)}
                      data-testid={`country-${country}`}
                    >
                      {country}
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* Cities */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-2">
                <Building className="w-4 h-4" />
                Cities
              </Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {availableAreas.cities.length === 0 ? (
                  <span className="text-xs text-zinc-500">No cities available yet</span>
                ) : (
                  availableAreas.cities.map(city => (
                    <Badge
                      key={city}
                      variant={settings.cities.includes(city) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        settings.cities.includes(city)
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                      }`}
                      onClick={() => handleToggleCity(city)}
                      data-testid={`city-${city}`}
                    >
                      {city}
                    </Badge>
                  ))
                )}
              </div>
            </div>
          </section>
        )}

        {/* Category Filters */}
        {settings.enabled && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 space-y-4">
            <div className="flex items-center gap-3">
              <Tag className="w-5 h-5 text-violet-500" />
              <div>
                <h3 className="font-medium text-zinc-100">Category Filters</h3>
                <p className="text-xs text-zinc-500">Select categories to receive alerts for (empty = all)</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {availableCategories.map(category => (
                <Badge
                  key={category}
                  variant={settings.categories.includes(category) ? "default" : "outline"}
                  className={`cursor-pointer transition-colors ${
                    settings.categories.includes(category)
                      ? 'bg-violet-600 hover:bg-violet-700 text-white'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                  }`}
                  onClick={() => handleToggleCategory(category)}
                  data-testid={`category-filter-${category}`}
                >
                  {category}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Test Notification */}
        {settings.enabled && hasPushSubscription && (
          <section className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Send className="w-5 h-5 text-emerald-500" />
                <div>
                  <h3 className="font-medium text-zinc-100">Test Notification</h3>
                  <p className="text-xs text-zinc-500">Send yourself a test notification</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTestNotification}
                disabled={isSendingTest}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="test-notification-btn"
              >
                {isSendingTest ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Send Test'
                )}
              </Button>
            </div>
          </section>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSaveSettings}
          disabled={isSaving}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg"
          data-testid="save-settings-btn"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </Button>
      </main>
    </div>
  );
};
